import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { EducationHostedFormModel, EducationHostedFormSteps } from '../EducationHostedForm.types'
import { useOrganisationField } from '../hooks/useOrganisationField'
import { useNavigateForm } from '../hooks/useNavigateForm'
import { HasCurrentPageErrorsProps, useHasCurrentPageErrors } from '../hooks/useHasCurrentPageErrors'

import { Step } from '@percent/education-hosted-form/ui/Step/Step'
import { useTrackEvent } from '@percent/education-hosted-form/analytics/useTrackEvent'
import { FormField, Flex } from '@percent/lemonade'
import { useEducationHostedForm } from '@percent/education-hosted-form/hooks/useEducationHostedForm/useEducationHostedForm'
import { CountrySelect } from '@percent/education-hosted-form/ui/CountrySelect/CountrySelect'
import {
  FormState,
  SelectOption
} from '@percent/education-hosted-form/context/educationHostedForm/EducationHostedFormContext.types'
import { OrganisationSelect } from '@percent/education-hosted-form/ui/OrganisationSelect/OrganisationSelect'

export function SelectOrganisation() {
  const { t } = useTranslation()
  const { progress, setFormState, partnerConfig } = useEducationHostedForm()
  const supportedCountries = partnerConfig?.configuration.education?.supportedCountries
  const { setOrganisationValue, setQueryValue, organisation, query } = useOrganisationField()
  const { handleNextStep } = useNavigateForm()
  const trackEvent = useTrackEvent()

  const { control, setValue } = useFormContext<EducationHostedFormModel>()
  const country = useWatch({ control, name: 'country' })

  const inputFields: HasCurrentPageErrorsProps = [
    {
      fieldKey: 'country'
    },
    {
      fieldKey: 'organisationId'
    }
  ]

  const { hasPageErrors, errors, getStatus } = useHasCurrentPageErrors(inputFields)

  const { country: countryError, organisationId: organisationError } = errors

  const onCountryChange = (countryOption: SelectOption) => {
    // shouldValidate, or the page-load required error sticks and Next stays disabled.
    setValue('country', countryOption.value, { shouldValidate: true })

    // If the organisation field is present, we want to clear it when the country changes
    setOrganisationValue(undefined)
    setQueryValue('')
    setValue('organisationId', '', { shouldValidate: true, shouldTouch: false })
  }

  const handleNext = () => {
    setFormState(FormState.DIRTY)
    handleNextStep(EducationHostedFormSteps.AGENT_DETAILS)
  }

  const handleOrgChange = (newValue: SelectOption) => {
    setValue('organisationName', undefined)
    setValue('organisationWebsite', undefined)
    setValue('organisationAddressLine1', undefined)
    setValue('organisationAddressLine2', undefined)
    setValue('organisationCity', undefined)
    setValue('organisationState', undefined)
    setValue('organisationPostalCode', undefined)
    setValue('organisationId', newValue.value, { shouldValidate: true, shouldTouch: true })
  }

  const handleAddOrganisation = () => {
    trackEvent('education_form_button_clicked', {
      button: 'add_organisation',
      step: EducationHostedFormSteps.SELECT_ORGANISATION
    })
    // Clear organisationId so the schema's `!id` predicate recognises the add-organisation flow.
    // Also clear the dropdown's own state so back-navigation from AddOrganisation shows
    // an empty dropdown instead of a stale selection that conflicts with the disabled Next.
    setOrganisationValue(undefined)
    setQueryValue('')
    setValue('organisationId', '', { shouldValidate: true, shouldTouch: false })
    setFormState(FormState.DIRTY)
    handleNextStep(EducationHostedFormSteps.ADD_ORGANISATION)
  }

  return (
    <Step
      step={EducationHostedFormSteps.SELECT_ORGANISATION}
      title={t('selectOrganisation.title')}
      description={t('selectOrganisation.description')}
      onNext={handleNext}
      nextButtonDisabled={hasPageErrors}
      nextButtonLabel={t('button.next')}
      progress={progress}
    >
      <Flex direction="column" gap={16} w="100%">
        <FormField
          label={t('selectOrganisation.country')}
          status={getStatus(countryError)}
          necessity="required"
          statusMessage={countryError}
        >
          <CountrySelect
            name="country"
            placeholder={t('selectOrganisation.countryPlaceholder')}
            value={country}
            onChange={onCountryChange}
            supportedCountries={supportedCountries}
            // Lock the selector only when the config allows a single country
            isDisabled={supportedCountries?.length === 1}
          />
        </FormField>
        <FormField
          label={t('selectOrganisation.organisation')}
          status={getStatus(organisationError)}
          necessity="required"
          statusMessage={organisationError}
        >
          <OrganisationSelect
            name="organisationId"
            placeholder={t('selectOrganisation.organisationPlaceholder')}
            onChange={handleOrgChange}
            onAddOrganisation={handleAddOrganisation}
            setOrganisationValue={setOrganisationValue}
            setQueryValue={setQueryValue}
            countryCode={country}
            query={query}
            organisation={organisation}
          />
        </FormField>
      </Flex>
    </Step>
  )
}
